import { supabase, unwrap, invokeFunction, apiPost, DbError, fetchAllRows, deleteRecordingFiles } from '../supabase';
import { config } from '../config';
import { parseDbDate } from '../dates';
import { randomDigits } from '../utils';
import type {
  DeviceState,
  Employee,
  EmployeeControls,
  EmployeeDataStats,
  PermissionKey,
  ServiceKey,
} from '../types';

export const SERVICE_DEFAULTS: Record<ServiceKey, boolean> = {
  track_calls: true,
  upload_recording: true,
  sync_metadata: true,
  sales_access: false,
};

export const PERMISSION_DEFAULTS: Record<PermissionKey, boolean> = {
  services: true,
  tracked_sim: true,
  start_date: true,
  exclude_new: true,
  recording_path_change: true,
  force_mobile_data_upload: false,
  easy_disconnect: true,
};

export const SERVICE_OPTIONS: { key: ServiceKey; label: string; description: string }[] = [
  {
    key: 'track_calls',
    label: 'Call tracking',
    description: 'Sync incoming and outgoing calls from this phone.',
  },
  {
    key: 'upload_recording',
    label: 'Recording upload',
    description: 'Upload call recordings to your cloud storage.',
  },
  {
    key: 'sync_metadata',
    label: 'Names, notes & labels sync',
    description: 'Keep contact details in sync between the app and dashboard.',
  },
  {
    key: 'sales_access',
    label: 'Sales CRM (beta)',
    description: 'Let this employee use the Sales CRM in the app.',
  },
];

export const PERMISSION_OPTIONS: { key: PermissionKey; label: string; description: string }[] = [
  {
    key: 'services',
    label: 'Pause tracking',
    description: 'Employee can switch tracking and uploads off in the app.',
  },
  {
    key: 'tracked_sim',
    label: 'Choose tracked SIM',
    description: 'Employee can pick which SIM is tracked on dual-SIM phones.',
  },
  {
    key: 'start_date',
    label: 'Change tracking start date',
    description: 'Employee can move the date tracking starts from.',
  },
  {
    key: 'exclude_new',
    label: 'Exclude personal contacts',
    description: 'Employee can add numbers that should never be tracked.',
  },
  {
    key: 'recording_path_change',
    label: 'Change recordings folder',
    description: 'Employee can point the app at a different recordings folder.',
  },
  {
    key: 'force_mobile_data_upload',
    label: 'Upload on mobile data',
    description: 'Recordings upload on mobile data, not only Wi-Fi.',
  },
  {
    key: 'easy_disconnect',
    label: 'Show disconnect option',
    description: 'Employee sees a “Disconnect” button in the app.',
  },
];

export function controlValue(controls: EmployeeControls, category: 'services', key: ServiceKey): boolean;
export function controlValue(
  controls: EmployeeControls,
  category: 'permissions',
  key: PermissionKey,
): boolean;
export function controlValue(
  controls: EmployeeControls,
  category: 'services' | 'permissions',
  key: string,
): boolean {
  const stored = (controls?.[category] as Record<string, unknown> | undefined)?.[key];
  if (typeof stored === 'boolean') return stored;
  return category === 'services'
    ? (SERVICE_DEFAULTS[key as ServiceKey] ?? true)
    : (PERMISSION_DEFAULTS[key as PermissionKey] ?? true);
}

const DAY_MS = 86_400_000;

export function deviceState(emp: Employee): DeviceState {
  if (!emp.token) return 'unpaired';
  const last = parseDbDate(emp.lastSync);
  return last && Date.now() - last.getTime() < DAY_MS ? 'active' : 'idle';
}

const toStr = (v: unknown) => (v === null || v === undefined || v === '' ? null : String(v));

function parseDeviceProfile(value: unknown, deviceId: string | null): Employee['deviceProfile'] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  // A new pairing may still have the previous handset's last telemetry.
  if (typeof row.reportedDeviceId === 'string' && row.reportedDeviceId !== deviceId) return null;
  return {
    manufacturer: toStr(row.manufacturer) ?? undefined,
    defaultDialerPackage: toStr(row.defaultDialerPackage) ?? undefined,
    defaultDialerName: toStr(row.defaultDialerName) ?? undefined,
    odialerInstalled: typeof row.odialerInstalled === 'boolean' ? row.odialerInstalled : undefined,
    reportedAt: toStr(row.reportedAt) ?? undefined,
    syncEnabled: typeof row.syncEnabled === 'boolean' ? row.syncEnabled : undefined,
    backgroundAllowed: typeof row.backgroundAllowed === 'boolean' ? row.backgroundAllowed : undefined,
    callLogPermission: typeof row.callLogPermission === 'boolean' ? row.callLogPermission : undefined,
    phonePermission: typeof row.phonePermission === 'boolean' ? row.phonePermission : undefined,
    audioPermission: typeof row.audioPermission === 'boolean' ? row.audioPermission : undefined,
    recordingFolderAccessible:
      typeof row.recordingFolderAccessible === 'boolean' ? row.recordingFolderAccessible : undefined,
  };
}

/** Reads employees.app_install ({ at, device }), written when the app opens with the code. */
function readAppInstall(raw: unknown): { at: string | null; device: string | null } {
  const value = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const text = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);
  return { at: text(value.at), device: text(value.device) };
}

export function mapEmployee(row: Record<string, unknown>): Employee {
  const controls = (row.controls && typeof row.controls === 'object' ? row.controls : {}) as EmployeeControls;
  const install = readAppInstall(row.app_install);
  return {
    id: Number(row.id),
    name: String(row.name || 'Unnamed'),
    email: toStr(row.email),
    phone: toStr(row.phone),
    pairingCode: toStr(row.pairing_code),
    pairingCodeExpiresAt: toStr(row.pairing_code_expires_at),
    token: toStr(row.token),
    fcmToken: toStr(row.fcm_token),
    deviceId: toStr(row.device_id),
    deviceModel: toStr(row.device_model),
    appInstalledAt: install.at,
    appInstallDevice: install.device,
    deviceProfile: parseDeviceProfile(row.device_profile, toStr(row.device_id)),
    devicePhone: toStr(row.device_phone),
    osVersion: toStr(row.os_version),
    batteryLevel:
      row.battery_level === null || row.battery_level === undefined ? null : Number(row.battery_level),
    lastSync: toStr(row.last_sync),
    controls: { ...controls, services: controls.services ?? {}, permissions: controls.permissions ?? {} },
    trackingStartDate: toStr(row.default_tracking_starting_date)?.slice(0, 10) ?? null,
    createdAt: toStr(row.created_at),
  };
}

export async function listEmployees(orgId: string): Promise<Employee[]> {
  const rows = unwrap(
    await supabase
      .from('employees')
      .select('*')
      .eq('org_id', orgId)
      .or('is_archived.is.null,is_archived.eq.false')
      .order('name', { ascending: true }),
  ) as Record<string, unknown>[];
  return (rows ?? []).map(mapEmployee);
}

export interface EmployeeInput {
  name: string;
  email?: string;
  trackingStartDate?: string;
}

export async function createEmployee(orgId: string, input: EmployeeInput): Promise<Employee> {
  const email = input.email?.trim().toLowerCase() || null;
  if (email) {
    const dup = unwrap(
      await supabase
        .from('employees')
        .select('id')
        .eq('org_id', orgId)
        .eq('email', email)
        .or('is_archived.is.null,is_archived.eq.false')
        .limit(1),
    ) as unknown[];
    if (dup?.length) throw new DbError('Someone on your team already uses this email.');
  }

  for (let attempt = 0; attempt < 4; attempt++) {
    const now = new Date().toISOString();
    const res = await supabase
      .from('employees')
      .insert({
        org_id: orgId,
        name: input.name.trim(),
        email,
        default_tracking_starting_date: input.trackingStartDate || null,
        pairing_code: randomDigits(6),
        created_at: now,
        updated_at: now,
      })
      .select()
      .single();
    if (!res.error) return mapEmployee(res.data as Record<string, unknown>);
    // Pairing codes are unique — a collision just means roll again.
    if (res.error.code === '23505' && /pairing/i.test(res.error.message)) continue;
    // The seat check runs in the database, so a stale page can still reach it.
    if (/employee capacity reached/i.test(res.error.message))
      throw new DbError('All phone seats are in use. Add a seat in Plan & billing to add this person.', res.error.code);
    throw new DbError(res.error.message, res.error.code);
  }
  throw new DbError('Could not generate a unique pairing code. Please try again.');
}

export async function updateEmployee(
  orgId: string,
  id: number,
  input: Partial<EmployeeInput>,
): Promise<void> {
  const update: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (input.name !== undefined) update.name = input.name.trim();
  if (input.email !== undefined) update.email = input.email.trim().toLowerCase() || null;
  if (update.email) {
    // Same rule as adding a person: one active person per email.
    const dup = unwrap(
      await supabase
        .from('employees')
        .select('id')
        .eq('org_id', orgId)
        .eq('email', update.email as string)
        .neq('id', id)
        .or('is_archived.is.null,is_archived.eq.false')
        .limit(1),
    ) as unknown[];
    if (dup?.length) throw new DbError('Someone on your team already uses this email.');
  }
  if (input.trackingStartDate !== undefined)
    update.default_tracking_starting_date = input.trackingStartDate || null;
  unwrap(await supabase.from('employees').update(update).eq('id', id).eq('org_id', orgId));
}

export async function setEmployeeControl(
  orgId: string,
  employee: Employee,
  category: 'services' | 'permissions',
  key: ServiceKey | PermissionKey,
  value: boolean,
): Promise<EmployeeControls> {
  const next: EmployeeControls = {
    ...employee.controls,
    services: { ...employee.controls.services },
    permissions: { ...employee.controls.permissions },
  };
  (next[category] as Record<string, boolean>)[key] = value;
  unwrap(
    await supabase
      .from('employees')
      .update({ controls: next, updated_at: new Date().toISOString() })
      .eq('id', employee.id)
      .eq('org_id', orgId),
  );
  return next;
}

export async function unpairEmployee(orgId: string, id: number): Promise<void> {
  await issuePairingCode(orgId, id, true);
}

/** Renew a consumed/expired code without unlinking a phone paired in another tab. */
export async function renewPairingCode(orgId: string, id: number): Promise<Employee> {
  return issuePairingCode(orgId, id, false);
}

async function issuePairingCode(orgId: string, id: number, unlink: boolean): Promise<Employee> {
  for (let attempt = 0; attempt < 4; attempt++) {
    let update = supabase
      .from('employees')
      .update({
        pairing_code: randomDigits(6),
        ...(unlink ? { token: null, device_id: null, fcm_token: null } : {}),
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .eq('org_id', orgId)
      .or('is_archived.is.null,is_archived.eq.false');
    if (!unlink) update = update.is('token', null);
    const result = await update.select('*').maybeSingle();
    if (result.error?.code === '23505' && /pairing/i.test(result.error.message)) continue;
    const row = unwrap(result) as Record<string, unknown> | null;
    if (!row) throw new DbError('Phone status changed. Refresh the team and try again.');
    return mapEmployee(row);
  }
  throw new DbError('Could not generate a unique pairing code. Please try again.');
}

export async function archiveEmployee(orgId: string, id: number): Promise<void> {
  unwrap(
    await supabase
      .from('employees')
      .update({ is_archived: true, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('org_id', orgId),
  );
}

/** Deletes the person's recording files first, then the person and (by cascade) their calls. */
export async function deleteEmployee(orgId: string, id: number): Promise<void> {
  const recordings = await fetchAllRows<{ recording_url: string | null }>((from, to) =>
    supabase
      .from('call_log')
      .select('recording_url')
      .eq('org_id', orgId)
      .eq('employee_id', id)
      .not('recording_url', 'is', null)
      .order('id')
      .range(from, to),
  );
  // Strict: rows go only once every file is confirmed gone, so no playable link outlives them.
  await deleteRecordingFiles(
    recordings.map((r) => r.recording_url),
    { strict: true },
  );
  unwrap(await supabase.from('employees').delete().eq('id', id).eq('org_id', orgId));
}

export async function getEmployeeDataStats(id: number): Promise<EmployeeDataStats> {
  const data = unwrap(await supabase.rpc('get_employee_stats', { p_employee_id: id })) as Record<
    string,
    unknown
  > | null;
  const n = (k: string) => Number(data?.[k]) || 0;
  return {
    calls: n('calls_count'),
    recordings: n('recordings_count'),
    contacts: n('contacts_count'),
    recordingBytes: n('recordings_size_bytes'),
    lastCallAt: toStr(data?.last_call_at),
    recentConnected: n('recent_connected'),
    recentRecorded: n('recent_recorded'),
    recentNotFound: n('recent_not_found'),
    waitingUploads: n('waiting_uploads'),
  };
}

export type WakeResult = { ok: true } | { ok: false; reason: 'unregistered' | 'failed'; message: string };

/** Push a silent wake-up so the app syncs now. */
export async function wakeDevice(id: number): Promise<WakeResult> {
  const data = await invokeFunction<{ success?: boolean; device_unregistered?: boolean; message?: string }>(
    'fcm-reviver',
    {
      employee_id: id,
    },
  );
  if (data?.device_unregistered) {
    return {
      ok: false,
      reason: 'unregistered',
      message: 'The app on this phone is uninstalled or signed out.',
    };
  }
  if (data?.success !== true) {
    return {
      ok: false,
      reason: 'failed',
      message: data?.message || 'Could not confirm the sync request. Try again.',
    };
  }
  return { ok: true };
}

export async function sendSetupEmail(input: {
  employeeId: number;
  email: string;
  name: string;
  pairingCode: string;
  orgName: string;
}): Promise<void> {
  if (config.isSandbox || config.recordingsApi) {
    // One ID per click: the server sends every click, but not a retried request twice.
    await apiPost('/api/employees/setup-email', { employeeId: input.employeeId, requestId: crypto.randomUUID() });
    return;
  }
  await invokeFunction('send-setup-instructions', {
    email: input.email,
    name: input.name,
    pairingCode: input.pairingCode,
    orgName: input.orgName,
  });
}
